/**
 * Connect-failure classification — shared by the kiosk (web-device) and the
 * student PWA (web-student).
 *
 * Why this lives in `shared/`: both apps hit the SAME failure modes (token
 * mint, LiveKit signalling, ICE/TURN) and both need a STABLE, non-localized
 * code they can (a) render for the user to quote to support and (b) ship to
 * `/client/log` for remote debugging. Keeping one classifier means a code seen
 * in the logs always means the same thing regardless of which app produced it.
 *
 * Invariants:
 *  - PURE + dependency-free: no `livekit-client`, no DOM requirement, SSR-safe.
 *    LiveKit types are duck-typed / compared by their stable numeric protocol
 *    values so `shared` never grows a media dependency.
 *  - NEVER throws. A classifier that blows up inside a `catch` would replace a
 *    diagnosable failure with an undiagnosable one.
 *  - NEVER returns secrets. `detail` is the error's own message only; callers
 *    that ship it must still redact (see `redact.ts`).
 */

// ─────────────────────────────────────────────────────────────────────────────
// Kiosk startup classification (moved here from web-device/status.svelte.ts so
// there is exactly one implementation; web-device re-exports these).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Machine-readable reason for a kiosk startup failure. Never localized — this
 * is the identifier the kiosk shell forwards to the diagnostics endpoint.
 *
 * `token_http_<status>` carries the backend's HTTP status verbatim
 * (e.g. `token_http_502`).
 */
export type StatusCode =
  | "token_fetch_failed"
  | `token_http_${number}`
  | "token_invalid"
  | "livekit_ws_failed"
  | "media_blocked"
  | "ice_failed"
  | "unknown";

/**
 * Which startup step threw. The same Error type can mean very different things
 * depending on the step, so the caller tells us where it came from.
 */
export type StartupPhase = "token" | "connect";

/**
 * Classify a startup failure into a stable {@link StatusCode} for diagnostics.
 *
 * `httpStatus` is the status of the LAST token HTTP response observed by the
 * caller's fetch wrapper, or `null` when the request never produced a response
 * (DNS failure, connection refused, TLS error — `fetch` rejects with a
 * TypeError). That distinction is what separates a dead backend/network
 * (`token_fetch_failed`) from a backend that answered with an error
 * (`token_http_<status>`).
 */
export function classifyStartupError(
  err: unknown,
  phase: StartupPhase,
  httpStatus: number | null = null,
): StatusCode {
  // `room.connect` rejected: signalling websocket never came up (bad URL, TLS,
  // proxy, LiveKit down).
  if (phase === "connect") return "livekit_ws_failed";

  // The backend ANSWERED, just not with a 2xx.
  if (httpStatus !== null && (httpStatus < 200 || httpStatus >= 300)) {
    return `token_http_${httpStatus}`;
  }

  const msg = err instanceof Error ? err.message : String(err ?? "");

  // Fallback when no wrapper observed the response: fetchToken's own message.
  const m = /token request failed:\s*(\d{3})/.exec(msg);
  if (m) return `token_http_${Number(m[1])}`;

  // 2xx but the body wasn't a usable token response, or the body was not JSON
  // at all (`res.json()` rejects with a SyntaxError).
  if (/invalid token response/i.test(msg)) return "token_invalid";
  if (err instanceof SyntaxError) return "token_invalid";

  // `fetch` itself rejected — DNS failure, connection refused, TLS error, CORS.
  // The platform always uses a TypeError for these.
  if (err instanceof TypeError) return "token_fetch_failed";
  if (/failed to fetch|networkerror|load failed/i.test(msg)) {
    return "token_fetch_failed";
  }

  return "unknown";
}

/**
 * LiveKit `DisconnectReason` values (from `@livekit/protocol`) that mean the
 * MEDIA PATH failed rather than the session ending normally — i.e. ICE never
 * established / stopped flowing, the classic symptom of a blocked TURN relay or
 * a firewall eating UDP.
 *
 * Compared numerically so `shared` needs no protocol dependency. Verified
 * against @livekit/protocol 1.46.6: only CONNECTION_TIMEOUT (14) and
 * MEDIA_FAILURE (15) indicate media/ICE loss; SIGNAL_CLOSE / CLIENT_INITIATED /
 * SERVER_SHUTDOWN etc. are normal drops.
 */
const DISCONNECT_CONNECTION_TIMEOUT = 14;
const DISCONNECT_MEDIA_FAILURE = 15;

/** True when a LiveKit `DisconnectReason` indicates an ICE/media failure. */
export function isIceFailureDisconnectReason(reason: unknown): boolean {
  if (typeof reason !== "number") return false;
  return (
    reason === DISCONNECT_CONNECTION_TIMEOUT ||
    reason === DISCONNECT_MEDIA_FAILURE
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Student-facing connect-error classification
// ─────────────────────────────────────────────────────────────────────────────

/** Coarse bucket — drives the short status-bar label and the support triage. */
export type ConnectErrorCategory =
  | "server_error"
  | "auth_expired"
  | "e2ee_failed"
  | "ice_failed"
  | "turn_unreachable"
  | "network_offline"
  | "unknown";

/**
 * Stable, NEVER-localized code shown in the UI so a student can quote it and
 * shipped verbatim to `/client/log`. More specific than the category.
 */
export type ConnectErrorCode =
  | "ROOM_OFFLINE"
  | "SESSION_LIMIT"
  | "SERVER_BUSY"
  | "SERVER_ERROR"
  | "SIGNAL_UNREACHABLE"
  | "AUTH_EXPIRED"
  | "E2EE_FAILED"
  | "ICE_FAILED"
  | "TURN_UNREACHABLE"
  | "NETWORK_OFFLINE"
  | "UNKNOWN";

export interface ConnectErrorInfo {
  category: ConnectErrorCategory;
  /** Stable code for the UI + logs. Never translate. */
  code: ConnectErrorCode;
  /** i18n key for the user-facing, actionable message. */
  messageKey: `error.connect.${string}`;
  /** i18n key for the short status-bar label. */
  shortKey: `error.connect.short.${ConnectErrorCategory}`;
  /** HTTP status when the backend answered, else null. */
  httpStatus: number | null;
  /**
   * Raw technical detail for support (the error's own message, trimmed). NOT
   * localized, NOT redacted — the caller redacts before shipping.
   */
  detail: string;
}

export interface ConnectErrorContext {
  /**
   * `navigator.onLine` at the time of the failure. `undefined` ⇒ unknown, the
   * classifier then never claims "offline". Passed in so the classifier stays
   * DOM-free and unit-testable.
   */
  online?: boolean;
  /**
   * Number of ICE servers that were actually configured on the PeerConnection.
   * `0` turns an ICE failure into `TURN_UNREACHABLE` (no relay was even
   * available), which is a different remediation than "your network blocks the
   * relay we handed you".
   */
  iceServerCount?: number;
}

const CATEGORY_OF: Record<ConnectErrorCode, ConnectErrorCategory> = {
  ROOM_OFFLINE: "server_error",
  SESSION_LIMIT: "server_error",
  SERVER_BUSY: "server_error",
  SERVER_ERROR: "server_error",
  SIGNAL_UNREACHABLE: "server_error",
  AUTH_EXPIRED: "auth_expired",
  E2EE_FAILED: "e2ee_failed",
  ICE_FAILED: "ice_failed",
  TURN_UNREACHABLE: "turn_unreachable",
  NETWORK_OFFLINE: "network_offline",
  UNKNOWN: "unknown",
};

/** `ROOM_OFFLINE` → `error.connect.roomOffline`. */
function messageKeyFor(code: ConnectErrorCode): `error.connect.${string}` {
  const camel = code
    .toLowerCase()
    .replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
  return `error.connect.${camel}`;
}

function info(
  code: ConnectErrorCode,
  detail: string,
  httpStatus: number | null = null,
): ConnectErrorInfo {
  const category = CATEGORY_OF[code];
  return {
    category,
    code,
    messageKey: messageKeyFor(code),
    shortKey: `error.connect.short.${category}`,
    httpStatus,
    detail,
  };
}

/** The error's own message, safely stringified and length-bounded. */
function detailOf(err: unknown): string {
  let raw: string;
  try {
    if (err instanceof Error) raw = `${err.name}: ${err.message}`;
    else if (typeof err === "string") raw = err;
    else raw = JSON.stringify(err) ?? String(err);
  } catch {
    raw = "unserializable error";
  }
  raw = (raw ?? "").trim();
  return raw.length > 300 ? `${raw.slice(0, 300)}…` : raw;
}

/** Duck-typed read of a property; never throws. */
function prop(obj: unknown, key: string): unknown {
  if (obj === null || typeof obj !== "object") return undefined;
  try {
    return (obj as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

/**
 * Recognise our own `ApiError` (backend answered non-2xx) WITHOUT importing it:
 * `shared` must not depend on an app's api client, and LiveKit's own
 * `ConnectionError` also carries a numeric `status` we must not confuse it with.
 */
function apiErrorStatus(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const status = prop(err, "status");
  if (typeof status !== "number") return null;
  if (err.name === "ApiError") return status;
  // Fallback for structurally-identical errors: an `ApiError` always carries a
  // (possibly null) parsed `body`; LiveKit errors never do.
  if (err.name !== "ConnectionError" && "body" in (err as object)) return status;
  return null;
}

/** The backend's structured `{ error: "..." }` discriminator, when present. */
function apiErrorTag(err: unknown): string | null {
  const body = prop(err, "body");
  const tag = prop(body, "error");
  return typeof tag === "string" ? tag : null;
}

/**
 * Classify a join/connect failure into a stable {@link ConnectErrorInfo}.
 *
 * Order matters: a backend that ANSWERED (ApiError) always wins, because that
 * is the most specific signal we have. Only then do we fall back to reading
 * LiveKit / platform error shapes and finally the message text.
 *
 * Never throws — any internal failure degrades to `UNKNOWN`.
 */
export function classifyConnectError(
  err: unknown,
  ctx: ConnectErrorContext = {},
): ConnectErrorInfo {
  try {
    const detail = detailOf(err);

    // ── 1. The backend answered with an error ────────────────────────────────
    const status = apiErrorStatus(err);
    if (status !== null) {
      const tag = apiErrorTag(err);
      if (status === 401) return info("AUTH_EXPIRED", detail, status);
      if (tag === "e2ee_required") return info("E2EE_FAILED", detail, status);
      if (tag === "room_offline") return info("ROOM_OFFLINE", detail, status);
      if (tag === "session_limit") return info("SESSION_LIMIT", detail, status);
      if (status === 409) return info("SERVER_BUSY", detail, status);
      return info("SERVER_ERROR", detail, status);
    }

    const msg = err instanceof Error ? err.message : String(err ?? "");
    const name = err instanceof Error ? err.name : "";

    // ── 2. E2EE ──────────────────────────────────────────────────────────────
    // The LiveKit e2ee worker raises `CryptorError`/`EncryptionError`; a missing
    // media key surfaces as "no key set"/"key not found" (frame-level).
    if (
      /encryptionerror|cryptorerror/i.test(name) ||
      /e2ee_required|no key set|key (is )?missing|missing key|decryption failed/i.test(
        msg,
      )
    ) {
      return info("E2EE_FAILED", detail);
    }

    // ── 3. The device is plainly offline ─────────────────────────────────────
    // Only trusted when the caller actually observed it (`online === false`).
    if (ctx.online === false) return info("NETWORK_OFFLINE", detail);

    // ── 4. ICE / TURN ────────────────────────────────────────────────────────
    // "could not establish pc connection" is livekit-client's own message when
    // the PeerConnection never reached `connected`; "PC manager is closed"
    // shows up when the engine is torn down mid-negotiation. Both mean the
    // media path never came up.
    if (
      /could not establish pc connection|ice (connection )?failed|iceconnectionstate.*failed|pc manager is closed|media failure|connection timeout|timed? ?out/i.test(
        msg,
      )
    ) {
      // No relay was configured at all ⇒ we never had a chance on a restrictive
      // network. Distinct remediation, so distinct code.
      if (ctx.iceServerCount === 0) return info("TURN_UNREACHABLE", detail);
      return info("ICE_FAILED", detail);
    }
    if (/\bturn\b|relay (server )?(unreachable|failed)/i.test(msg)) {
      return info("TURN_UNREACHABLE", detail);
    }

    // ── 5. Signalling / transport never reached the server ───────────────────
    // livekit-client ConnectionError variants (ServerUnreachable/WebSocket) and
    // the platform's TypeError for a rejected fetch.
    if (
      err instanceof TypeError ||
      /failed to fetch|networkerror|load failed/i.test(msg)
    ) {
      return info("SIGNAL_UNREACHABLE", detail);
    }
    if (
      name === "ConnectionError" ||
      /websocket|server unreachable|signal|handshake/i.test(msg)
    ) {
      return info("SIGNAL_UNREACHABLE", detail);
    }

    return info("UNKNOWN", detail);
  } catch {
    // A classifier must never turn a diagnosable failure into a crash.
    return info("UNKNOWN", "");
  }
}
